import { useEffect, useRef, useState } from 'react';

/* Tempo real do atendimento: uma conexão só por aba (/api/whatsapp/ws), compartilhada pela tela de Atendimento
 * e pelo sino do topo (AlertasWA).
 * - não depende do chat interno estar ligado;
 * - batimento: ping a cada 20 s; sem resposta em 10 s, a conexão é dada como morta e refeita (rede que cai sem
 *   avisar deixava a tela "conectada" sem receber nada até atualizar a página);
 * - ao (re)conectar, avisa quem usa (onReconectar) para buscar o que chegou no intervalo. */

function wsUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const ws = base.replace(/^http/, 'ws').replace(/\/$/, '');
  return `${ws}/api/whatsapp/ws?token=${encodeURIComponent(token)}`;
}

const ouvintes = new Set();      // { onEvent, onEstado, onReconectar }
let socket = null; let conectado = false; let tentativa = 0; let jaConectou = false;
let tRetry = null; let tPing = null; let tSemPong = null;

function avisarEstado(v) {
  conectado = v;
  ouvintes.forEach(o => o.onEstado?.(v));
}
function limparTimers() { clearTimeout(tRetry); clearInterval(tPing); clearTimeout(tSemPong); }

function abrir() {
  const token = localStorage.getItem('agrolink_token');
  if (!token || socket || !ouvintes.size) return;
  let ws;
  try { ws = new WebSocket(wsUrl(token)); } catch (_) { agendar(); return; }
  socket = ws;
  const morto = () => { try { ws.close(); } catch (_) { /* noop */ } };
  ws.onopen = () => {
    tentativa = 0;
    avisarEstado(true);
    const reconexao = jaConectou; jaConectou = true;
    ouvintes.forEach(o => o.onReconectar?.(reconexao));
    tPing = setInterval(() => {
      try { ws.send('{"type":"ping"}'); } catch (_) { morto(); return; }
      clearTimeout(tSemPong);
      tSemPong = setTimeout(morto, 10000);
    }, 20000);
  };
  ws.onmessage = (e) => {
    clearTimeout(tSemPong);   // qualquer coisa que chega prova que a conexão está viva
    let d; try { d = JSON.parse(e.data); } catch (_) { return; }
    if (d?.type?.startsWith('wa_')) ouvintes.forEach(o => o.onEvent?.(d));
  };
  ws.onclose = () => {
    if (socket === ws) socket = null;
    limparTimers();
    avisarEstado(false);
    agendar();
  };
  ws.onerror = morto;
}
function agendar() {
  clearTimeout(tRetry);
  if (!ouvintes.size) return;
  tRetry = setTimeout(abrir, Math.min(15000, 1000 * 2 ** tentativa++));
}
// voltou a internet ou a aba: tenta na hora em vez de esperar o próximo intervalo
if (typeof window !== 'undefined') {
  const agora = () => { if (!socket && ouvintes.size) { tentativa = 0; clearTimeout(tRetry); abrir(); } };
  window.addEventListener('online', agora);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) agora(); });
}

export function useWaRealtime(onEvent, ativo = true, onReconectar) {
  const [connected, setConnected] = useState(conectado);
  const eventRef = useRef(onEvent); eventRef.current = onEvent;
  const recRef = useRef(onReconectar); recRef.current = onReconectar;

  useEffect(() => {
    if (!ativo) return undefined;
    const o = {
      onEvent: (d) => eventRef.current?.(d),
      onEstado: setConnected,
      onReconectar: (re) => recRef.current?.(re),
    };
    ouvintes.add(o);
    setConnected(conectado);
    if (socket) { if (conectado) o.onReconectar(false); } else { tentativa = 0; abrir(); }
    return () => {
      ouvintes.delete(o);
      if (!ouvintes.size) { limparTimers(); const ws = socket; socket = null; jaConectou = false; try { ws && ws.close(); } catch (_) { /* noop */ } }
    };
  }, [ativo]);
  return connected;
}

/* Conversa aberta agora na tela de Atendimento: o sino não toca nem conta mensagens dela. */
let conversaAberta = null;
export const setConversaAberta = (jid) => { conversaAberta = jid || null; };
export const getConversaAberta = () => (document.hidden ? null : conversaAberta);

/* ---------- sons (gerados, sem arquivo) ---------- */
let ctx = null;
function audio() {
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  } catch (_) { return null; }
}
// o navegador só libera som depois de um clique/tecla na página: prepara o áudio na 1ª interação
if (typeof window !== 'undefined') {
  const liberar = () => { audio(); window.removeEventListener('pointerdown', liberar); window.removeEventListener('keydown', liberar); };
  window.addEventListener('pointerdown', liberar);
  window.addEventListener('keydown', liberar);
}
function notas(freqs, dur = 0.13, vol = 0.09) {
  const c = audio();
  if (!c) return;
  freqs.forEach((f, i) => {
    const t = c.currentTime + i * dur;
    const o = c.createOscillator(); const g = c.createGain();
    o.type = 'sine'; o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.95);
    o.connect(g); g.connect(c.destination); o.start(t); o.stop(t + dur);
  });
}
export const SONS = {
  mensagem: () => notas([880, 1175]),                  // dois toques
  transferencia: () => notas([660, 880, 1320], 0.15),  // três subindo
  mencao: () => notas([1175, 880, 1175], 0.11),
};
